(() => {
  'use strict';

  if (window.__awLocalizationInitialized) return;
  window.__awLocalizationInitialized = true;

  const STORAGE = Object.freeze({
    manual: 'aw_localization_manual_v1',
    storedAttempt: 'aw_localization_stored_attempt_v1',
    geoChoices: 'aw_localization_geo_choices_v1',
    legacyDismissed: 'aw_localization_recommendation_dismissed_v1',
    legacyAutoAttemptV1: 'aw_localization_auto_attempt_v1',
    legacyAutoAttemptV2: 'aw_localization_auto_attempt_v2',
  });

  const RESOLUTION_EVENT = 'aw:localization-resolved';
  const DISCOVERY_TIMEOUT_MS = 2600;
  const STORED_ATTEMPT_MS = 5 * 60 * 1000;
  const GEO_CHOICE_MAX_AGE_MS = 180 * 24 * 60 * 60 * 1000;
  const PROMPT_RETRY_MS = 450;
  const MAX_PROMPT_RETRIES = 120;
  const GERMAN_DIRECT_COUNTRIES = new Set(['DE', 'AT']);

  /*
    Binding first-visit policy for the bare antlitzwerk.de entry point:
    - Germany and Austria continue in German without a language question.
    - France receives a French recommendation.
    - Every other detected country receives an English recommendation.
    - A direct /fr or /en URL and every deliberate visitor choice always win.
    - Recommendation choices are remembered per detected country, never globally.
  */

  const COPY = Object.freeze({
    de: {
      close: 'Auf Deutsch bleiben',
      eyebrow: 'ANTLITZWERK · REGION & SPRACHE',
      title: 'Möchtest du die passende Sprachversion öffnen?',
      description:
        'Wir haben eine zu deinem Standort passende Ansicht vorbereitet. Du kannst sie öffnen oder auf der deutschen Seite bleiben.',
      current: 'Aktuelle Ansicht',
      recommended: 'Empfohlene Ansicht',
      recommendedLanguage: 'Empfohlene Sprache',
      accept: 'EMPFOHLENE VERSION ÖFFNEN',
      stay: 'AUF DEUTSCH BLEIBEN',
      note: 'Region und Sprache kannst du später jederzeit im Menü ändern.',
    },
    en: {
      close: 'Continue in German',
      eyebrow: 'ANTLITZWERK · REGION & LANGUAGE',
      title: 'Would you like to continue in English?',
      description:
        'We have prepared the English version for your location. Open it now or continue on the German site.',
      current: 'Current view',
      recommended: 'Recommended view',
      recommendedLanguage: 'Recommended language',
      accept: 'CONTINUE IN ENGLISH',
      stay: 'CONTINUE IN GERMAN',
      note: 'You can change your region and language at any time from the menu.',
    },
    fr: {
      close: 'Rester en allemand',
      eyebrow: 'ANTLITZWERK · RÉGION & LANGUE',
      title: 'Souhaitez-vous continuer en français ?',
      description:
        'Nous avons préparé la version française adaptée à votre localisation. Ouvrez-la maintenant ou poursuivez sur le site allemand.',
      current: 'Version actuelle',
      recommended: 'Version recommandée',
      recommendedLanguage: 'Langue recommandée',
      accept: 'CONTINUER EN FRANÇAIS',
      stay: 'RESTER EN ALLEMAND',
      note: 'Vous pourrez modifier la région et la langue à tout moment depuis le menu.',
    },
  });

  const FALLBACK_COUNTRY_NAMES = Object.freeze({
    de: {
      AT: 'Österreich',
      DE: 'Deutschland',
      FR: 'Frankreich',
      GB: 'Vereinigtes Königreich',
      US: 'Vereinigte Staaten',
    },
    en: { AT: 'Austria', DE: 'Germany', FR: 'France', GB: 'United Kingdom', US: 'United States' },
    fr: { AT: 'Autriche', DE: 'Allemagne', FR: 'France', GB: 'Royaume-Uni', US: 'États-Unis' },
  });

  const FALLBACK_LANGUAGE_NAMES = Object.freeze({
    de: { de: 'Deutsch', en: 'Englisch', fr: 'Französisch' },
    en: { de: 'German', en: 'English', fr: 'French' },
    fr: { de: 'Allemand', en: 'Anglais', fr: 'Français' },
  });

  const safeStorage = (storage) => ({
    get(key) {
      try {
        return storage?.getItem(key) ?? null;
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        storage?.setItem(key, String(value));
      } catch (error) {}
    },
    remove(key) {
      try {
        storage?.removeItem(key);
      } catch (error) {}
    },
  });

  const resolveStorage = (name) => {
    try {
      return window[name];
    } catch (error) {
      return null;
    }
  };

  const local = safeStorage(resolveStorage('localStorage'));
  const session = safeStorage(resolveStorage('sessionStorage'));

  const parseJSON = (value, fallback = null) => {
    if (!value) return fallback;
    try {
      return JSON.parse(value);
    } catch (error) {
      return fallback;
    }
  };

  const normalizeCode = (value) => String(value || '').trim().replace(/_/g, '-').toLowerCase();
  const baseCode = (value) => normalizeCode(value).split('-')[0];
  const upperCountry = (value) => String(value || '').trim().toUpperCase();

  const publishState = (detail = {}) => {
    const state = {
      status: detail.status || 'ready',
      targetCountry: upperCountry(detail.targetCountry),
      targetLanguage: baseCode(detail.targetLanguage),
      uiLanguage: baseCode(detail.uiLanguage || detail.targetLanguage),
      reason: detail.reason || '',
      emittedAt: Date.now(),
    };

    window.__awLocalizationState = state;
    window.dispatchEvent(new CustomEvent(RESOLUTION_EVENT, { detail: state }));
    return state;
  };

  const init = () => {
    const root = document.querySelector('[data-aw-localization-root]');
    const configElement = document.getElementById('AwLocalizationConfig');
    const form = document.getElementById('AwLocalizationForm');
    const countryInput = form?.querySelector('[data-aw-localization-country-input]');
    const languageInput = form?.querySelector('[data-aw-localization-language-input]');

    if (!root || !configElement || !form || !countryInput || !languageInput) {
      publishState({
        status: 'ready',
        targetCountry: window.Shopify?.country || '',
        targetLanguage: window.Shopify?.language || document.documentElement.lang || 'de',
        uiLanguage: window.Shopify?.language || document.documentElement.lang || 'de',
        reason: 'component-unavailable',
      });
      return;
    }

    const config = parseJSON(configElement.textContent, null);
    if (!config?.current || !Array.isArray(config.countries) || !Array.isArray(config.languages)) {
      publishState({
        status: 'ready',
        targetCountry: window.Shopify?.country || '',
        targetLanguage: window.Shopify?.language || document.documentElement.lang || 'de',
        uiLanguage: window.Shopify?.language || document.documentElement.lang || 'de',
        reason: 'invalid-config',
      });
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const resetRequested = params.get('aw_localization_reset') === '1';
    const previewRequested = params.get('aw_localization_preview');

    if (resetRequested) {
      Object.values(STORAGE).forEach((key) => {
        local.remove(key);
        session.remove(key);
      });
    }

    /* V1.0.17 used automatic redirects and a temporary dismissal record. They
       must not suppress the new explicit first-step decision. */
    local.remove(STORAGE.legacyDismissed);
    session.remove(STORAGE.legacyAutoAttemptV1);
    session.remove(STORAGE.legacyAutoAttemptV2);

    const countryMap = new Map(config.countries.map((country) => [upperCountry(country.code), country]));
    const languageMap = new Map(config.languages.map((language) => [normalizeCode(language.code), language]));

    const findSupportedLanguage = (requested) => {
      const normalized = normalizeCode(requested);
      if (!normalized) return null;
      if (languageMap.has(normalized)) return languageMap.get(normalized);

      const requestedBase = baseCode(normalized);
      for (const [code, language] of languageMap) {
        if (baseCode(code) === requestedBase) return language;
      }
      return null;
    };

    const current = {
      country: upperCountry(config.current.country),
      countryName: String(config.current.countryName || config.current.country || ''),
      language: baseCode(config.current.language),
      languageName: String(config.current.languageName || config.current.language || ''),
      rootUrl: String(config.current.rootUrl || '/'),
    };

    const currentRootPath = current.rootUrl.replace(/\/+$/, '') || '/';
    const pathHasExplicitLanguageRoot =
      currentRootPath !== '/' &&
      (window.location.pathname === currentRootPath || window.location.pathname.startsWith(`${currentRootPath}/`));

    const navigationEntry = (() => {
      try {
        return window.performance?.getEntriesByType?.('navigation')?.[0] || null;
      } catch (error) {
        return null;
      }
    })();
    const arrivedThroughRedirect = Number(navigationEntry?.redirectCount || 0) > 0;
    const directLocalizedEntry = pathHasExplicitLanguageRoot && !arrivedThroughRedirect;

    const copyLanguage = (requested) => {
      const language = baseCode(requested);
      return COPY[language] ? language : COPY[current.language] ? current.language : 'de';
    };

    const applyCopy = (requested) => {
      const language = copyLanguage(requested);
      const copy = COPY[language];

      root.dataset.awLocalizationLanguage = language;
      root.setAttribute('lang', language);
      root.querySelectorAll('[data-aw-localization-copy]').forEach((element) => {
        const key = element.dataset.awLocalizationCopy;
        if (key && copy[key]) element.textContent = copy[key];
      });
      root.querySelectorAll('[data-aw-localization-copy-aria]').forEach((element) => {
        const key = element.dataset.awLocalizationCopyAria;
        if (key && copy[key]) element.setAttribute('aria-label', copy[key]);
      });

      return language;
    };

    const displayName = (type, code, locale, fallback) => {
      try {
        if (typeof Intl.DisplayNames === 'function') {
          const names = new Intl.DisplayNames([locale], { type });
          const value = names.of(code);
          if (value) return value.charAt(0).toLocaleUpperCase(locale) + value.slice(1);
        }
      } catch (error) {}
      return fallback || code;
    };

    const contextLabel = (countryCode, languageCode, localeCode) => {
      const locale = copyLanguage(localeCode);
      const country = upperCountry(countryCode);
      const language = baseCode(languageCode);
      const countryFallback = FALLBACK_COUNTRY_NAMES[locale]?.[country] || countryMap.get(country)?.name || country;
      const languageFallback =
        FALLBACK_LANGUAGE_NAMES[locale]?.[language] || findSupportedLanguage(language)?.name || language;
      const countryName = displayName('region', country, locale, countryFallback);
      const languageName = displayName('language', language, locale, languageFallback);
      return [countryName, languageName].filter(Boolean).join(' · ');
    };

    const languageLabel = (languageCode, localeCode) => {
      const locale = copyLanguage(localeCode);
      const language = baseCode(languageCode);
      const fallback =
        FALLBACK_LANGUAGE_NAMES[locale]?.[language] || findSupportedLanguage(language)?.name || language;
      return displayName('language', language, locale, fallback);
    };

    let manualPreference = parseJSON(local.get(STORAGE.manual), null);

    /* V1.0.18 accidentally treated recommendation acceptance, recommendation
       dismissal and localized URLs as global manual preferences. That made an
       English recommendation accepted in the UK follow the same browser back
       to Germany, and could also make France inherit an older English state.
       Only a deliberate choice made in the theme's permanent selector is a
       global manual preference. Everything else is migrated away once. */
    const manualSource = String(manualPreference?.source || '');
    if (manualPreference && manualSource !== 'theme-selector') {
      local.remove(STORAGE.manual);
      manualPreference = null;
    }

    let hasManualPreference = Boolean(
      manualPreference?.source === 'theme-selector' && (manualPreference?.country || manualPreference?.language)
    );

    const readGeoChoices = () => {
      const parsed = parseJSON(local.get(STORAGE.geoChoices), {});
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    };

    const geoChoiceFor = (countryCode) => {
      const country = upperCountry(countryCode);
      if (!country) return null;
      const choices = readGeoChoices();
      const choice = choices[country];
      if (!choice || typeof choice !== 'object') return null;

      const savedAt = Number(choice.savedAt || 0);
      if (!savedAt || Date.now() - savedAt > GEO_CHOICE_MAX_AGE_MS) {
        delete choices[country];
        local.set(STORAGE.geoChoices, JSON.stringify(choices));
        return null;
      }

      return {
        country: upperCountry(choice.country || country),
        language: baseCode(choice.language),
        savedAt,
      };
    };

    const rememberGeoChoice = ({ detectedCountry, country, language }) => {
      const detected = upperCountry(detectedCountry);
      const normalizedLanguage = baseCode(language);
      if (!detected || !normalizedLanguage) return;

      const choices = readGeoChoices();
      choices[detected] = {
        country: upperCountry(country || detected),
        language: normalizedLanguage,
        savedAt: Date.now(),
      };
      local.set(STORAGE.geoChoices, JSON.stringify(choices));
    };

    const currentValue = root.querySelector('[data-aw-localization-current]');
    const recommendedValue = root.querySelector('[data-aw-localization-recommended]');
    const recommendedLabel = root.querySelector('[data-aw-localization-copy="recommended"]');
    const panel = root.querySelector('.aw-localization__panel');
    let recommendation = null;
    let promptRetryCount = 0;
    let promptResolveTimer = 0;

    const publishReady = ({ country = current.country, language = current.language, reason = 'ready' } = {}) =>
      publishState({
        status: 'ready',
        targetCountry: country,
        targetLanguage: language,
        uiLanguage: language,
        reason,
      });

    const submitLocalization = ({
      country,
      language,
      source = 'automatic',
      detectedCountry = country,
      reason = source,
    }) => {
      const supportedLanguage = findSupportedLanguage(language || current.language);
      const requestedCountry = upperCountry(country || current.country);
      const nextCountry = countryMap.has(requestedCountry) ? requestedCountry : current.country;
      const nextLanguage = baseCode(supportedLanguage?.code || current.language);

      if (!supportedLanguage || !countryMap.has(nextCountry)) return false;
      if (nextCountry === current.country && nextLanguage === current.language) return false;

      countryInput.value = nextCountry;
      languageInput.value = supportedLanguage.code;

      let methodInput = form.querySelector('input[name="_method"]');
      if (!methodInput) {
        methodInput = document.createElement('input');
        methodInput.type = 'hidden';
        methodInput.name = '_method';
        form.appendChild(methodInput);
      }
      methodInput.value = 'PUT';

      publishState({
        status: 'redirecting',
        targetCountry: detectedCountry || nextCountry,
        targetLanguage: nextLanguage,
        uiLanguage: nextLanguage,
        reason,
      });

      try {
        form.submit();
        return true;
      } catch (error) {
        publishReady({ reason: 'localization-submit-failed' });
        return false;
      }
    };

    const visibleElement = (element) => {
      if (!element || element === root || root.contains(element)) return false;
      if (element.hidden) return false;
      const style = window.getComputedStyle(element);
      const rect = element.getBoundingClientRect();
      return (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        Number(style.opacity || 1) !== 0 &&
        rect.width > 0 &&
        rect.height > 0
      );
    };

    const anotherOverlayIsOpen = () => {
      /* A hidden cookie banner waiting for this decision is not an open overlay. */
      if (document.documentElement.dataset.awCookieState === 'open') return true;

      const selectors = [
        '[data-aw-cookie]:not([hidden])',
        '[data-aw-cookie-preferences]:not([hidden])',
        '#shopify-pc__banner',
        '.shopify-pc__banner__dialog',
        '.shopify-pc__prefs__dialog',
        'dialog[open]',
        '[aria-modal="true"]',
        '[data-drawer][open]',
        '#awrp-root:not([hidden])',
      ];
      return Array.from(document.querySelectorAll(selectors.join(','))).some(visibleElement);
    };

    const showPrompt = () => {
      if (!recommendation || root.classList.contains('is-visible')) return;

      if (document.visibilityState !== 'visible' || anotherOverlayIsOpen()) {
        if (promptRetryCount < MAX_PROMPT_RETRIES) {
          promptRetryCount += 1;
          window.setTimeout(showPrompt, PROMPT_RETRY_MS);
        }
        return;
      }

      const uiLanguage = applyCopy(recommendation.uiLanguage || recommendation.language);
      if (currentValue) currentValue.textContent = contextLabel(current.country, current.language, uiLanguage);
      if (recommendedLabel && recommendation.languageOnly) {
        recommendedLabel.textContent = COPY[uiLanguage]?.recommendedLanguage || COPY.en.recommendedLanguage;
      }
      if (recommendedValue) {
        recommendedValue.textContent = recommendation.languageOnly
          ? languageLabel(recommendation.language, uiLanguage)
          : contextLabel(recommendation.country, recommendation.language, uiLanguage);
      }

      root.hidden = false;
      root.setAttribute('data-aw-localization-open', 'true');
      window.requestAnimationFrame(() => {
        root.classList.add('is-visible');
        window.setTimeout(() => panel?.focus({ preventScroll: true }), 80);
      });
    };

    const retryPromptWhenVisible = () => {
      if (document.visibilityState === 'visible' && recommendation && !root.classList.contains('is-visible')) {
        showPrompt();
      }
    };

    document.addEventListener('visibilitychange', retryPromptWhenVisible);
    window.addEventListener('focus', retryPromptWhenVisible);

    const openRecommendation = ({ country, language, uiLanguage, detectedCountry, reason, languageOnly = false }) => {
      const supportedLanguage = findSupportedLanguage(language);
      if (!supportedLanguage) {
        publishReady({ reason: `${reason}-language-unavailable` });
        return;
      }

      const requestedCountry = upperCountry(country || current.country);
      const targetCountry = countryMap.has(requestedCountry) ? requestedCountry : current.country;
      const targetLanguage = baseCode(supportedLanguage.code);
      const targetUiLanguage = copyLanguage(uiLanguage || targetLanguage);

      if (targetCountry === current.country && targetLanguage === current.language) {
        publishReady({ reason: `${reason}-already-current` });
        return;
      }

      recommendation = {
        country: targetCountry,
        language: targetLanguage,
        uiLanguage: targetUiLanguage,
        detectedCountry: upperCountry(detectedCountry || requestedCountry),
        reason,
        languageOnly: Boolean(languageOnly),
      };

      publishState({
        status: 'awaiting-choice',
        targetCountry,
        targetLanguage,
        uiLanguage: targetUiLanguage,
        reason,
      });

      window.setTimeout(showPrompt, previewRequested ? 120 : 180);
    };

    const closePromptAndContinueCurrent = (source) => {
      window.clearTimeout(promptResolveTimer);
      if (recommendation?.detectedCountry) {
        rememberGeoChoice({
          detectedCountry: recommendation.detectedCountry,
          country: current.country,
          language: current.language,
        });
      }
      root.classList.remove('is-visible');

      promptResolveTimer = window.setTimeout(() => {
        root.hidden = true;
        root.removeAttribute('data-aw-localization-open');
        publishReady({ reason: source });
      }, 440);
    };

    root.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const action = target?.closest('[data-aw-localization-action]')?.dataset.awLocalizationAction;
      if (!action || !recommendation) return;

      if (action === 'accept') {
        rememberGeoChoice({
          detectedCountry: recommendation.detectedCountry,
          country: recommendation.country,
          language: recommendation.language,
        });

        if (
          !submitLocalization({
            country: recommendation.country,
            language: recommendation.language,
            source: 'recommendation-accepted-v4',
            detectedCountry: recommendation.detectedCountry,
            reason: 'recommendation-accepted',
          })
        ) {
          closePromptAndContinueCurrent('recommendation-submit-unavailable');
        }
        return;
      }

      if (action === 'stay') {
        closePromptAndContinueCurrent('recommendation-stay-german');
        return;
      }

      if (action === 'dismiss') closePromptAndContinueCurrent('recommendation-dismissed-to-german');
    });

    panel?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closePromptAndContinueCurrent('recommendation-dismissed-to-german');
      }
    });

    window.addEventListener('aw:localization-manual', (event) => {
      const detail = event.detail || {};
      hasManualPreference = true;
      root.classList.remove('is-visible');
      root.hidden = true;
      publishState({
        status: 'redirecting',
        targetCountry: detail.country || current.country,
        targetLanguage: detail.language || current.language,
        uiLanguage: detail.language || current.language,
        reason: 'theme-selector',
      });
    });

    const browserPreferredLanguage = () => {
      const candidates =
        Array.isArray(navigator.languages) && navigator.languages.length ? navigator.languages : [navigator.language];

      for (const candidate of candidates) {
        const supported = findSupportedLanguage(candidate);
        if (supported) return supported;
      }
      return null;
    };

    const storedAttemptIsFresh = () => {
      const attempt = parseJSON(session.get(STORAGE.storedAttempt), null);
      if (!attempt?.at) return false;
      if (Date.now() - Number(attempt.at) > STORED_ATTEMPT_MS) {
        session.remove(STORAGE.storedAttempt);
        return false;
      }
      return true;
    };

    const applyStoredPreference = () => {
      if (!manualPreference) return false;
      const supportedLanguage = findSupportedLanguage(manualPreference.language || current.language);
      const requestedCountry = upperCountry(manualPreference.country || current.country);
      const storedCountry = countryMap.has(requestedCountry) ? requestedCountry : current.country;
      const storedLanguage = baseCode(supportedLanguage?.code || current.language);

      if (storedCountry === current.country && storedLanguage === current.language) {
        publishReady({ reason: 'manual-preference-current' });
        return true;
      }

      if (storedAttemptIsFresh()) {
        publishReady({ reason: 'manual-preference-loop-protected' });
        return true;
      }

      session.set(
        STORAGE.storedAttempt,
        JSON.stringify({ country: storedCountry, language: storedLanguage, at: Date.now() })
      );

      if (
        submitLocalization({
          country: storedCountry,
          language: storedLanguage,
          source: 'stored-preference',
          detectedCountry: storedCountry,
          reason: 'stored-preference',
        })
      ) {
        return true;
      }

      publishReady({ reason: 'manual-preference-unavailable' });
      return true;
    };

    const targetCountryForDetected = (detectedCountry) => {
      const detected = upperCountry(detectedCountry);
      if (countryMap.has(detected)) return detected;
      return current.country;
    };

    const applyGeoPolicy = (detectedCountryRaw, reason) => {
      const detectedCountry = upperCountry(detectedCountryRaw);

      if (!detectedCountry) return false;

      if (GERMAN_DIRECT_COUNTRIES.has(detectedCountry)) {
        const german = findSupportedLanguage('de');
        const targetCountry = targetCountryForDetected(detectedCountry);
        const needsGermanNormalization =
          german && (current.language !== 'de' || (countryMap.has(detectedCountry) && current.country !== targetCountry));

        if (needsGermanNormalization) {
          if (
            submitLocalization({
              country: targetCountry,
              language: german.code,
              source: 'automatic',
              detectedCountry,
              reason: 'geo-german-direct',
            })
          ) {
            return true;
          }
        }

        publishReady({
          country: targetCountry,
          language: german?.code || current.language,
          reason: `${reason}-german-direct`,
        });
        return true;
      }

      const rememberedChoice = geoChoiceFor(detectedCountry);
      if (rememberedChoice) {
        const rememberedCountry = countryMap.has(rememberedChoice.country)
          ? rememberedChoice.country
          : targetCountryForDetected(detectedCountry);
        const rememberedAlreadyCurrent =
          rememberedChoice.language === current.language && rememberedCountry === current.country;

        if (rememberedAlreadyCurrent) {
          publishReady({
            country: current.country,
            language: current.language,
            reason: `${reason}-geo-choice-current`,
          });
          return true;
        }

        if (
          submitLocalization({
            country: rememberedCountry,
            language: rememberedChoice.language,
            source: 'automatic',
            detectedCountry,
            reason: `${reason}-geo-choice`,
          })
        ) {
          return true;
        }
      }

      const targetLanguage = detectedCountry === 'FR' ? 'fr' : 'en';
      const targetCountry = targetCountryForDetected(detectedCountry);

      openRecommendation({
        country: targetCountry,
        language: targetLanguage,
        uiLanguage: targetLanguage,
        detectedCountry,
        reason: detectedCountry === 'FR' ? `${reason}-france-french` : `${reason}-international-english`,
        languageOnly: !countryMap.has(detectedCountry),
      });
      return true;
    };

    const fallbackDiscovery = (reason) => {
      /* Never reuse the currently active Shopify country as GeoIP fallback:
         it may simply be a remembered market from an earlier VPN/location.
         Browser language is only an emergency hint when GeoIP is unavailable. */
      const browserLanguage = baseCode(browserPreferredLanguage()?.code || '');
      if (browserLanguage === 'fr') {
        openRecommendation({
          country: countryMap.has('FR') ? 'FR' : current.country,
          language: 'fr',
          uiLanguage: 'fr',
          detectedCountry: 'FR',
          reason: `${reason}-browser-french-fallback`,
        });
        return;
      }

      if (browserLanguage === 'en') {
        openRecommendation({
          country: current.country,
          language: 'en',
          uiLanguage: 'en',
          detectedCountry: current.country,
          reason: `${reason}-browser-english-fallback`,
        });
        return;
      }

      publishReady({ reason: `${reason}-german-fallback` });
    };

    const querySuggestions = async () => {
      const rootUrl = window.Shopify?.routes?.root || current.rootUrl || '/';
      const endpoint = new URL(`${rootUrl}browsing_context_suggestions.json`, window.location.origin);
      endpoint.searchParams.set('country[enabled]', 'true');
      endpoint.searchParams.set('language[enabled]', 'true');

      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      const timeout = window.setTimeout(() => controller?.abort(), DISCOVERY_TIMEOUT_MS);

      try {
        const response = await fetch(endpoint.toString(), {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          cache: 'no-store',
          signal: controller?.signal,
        });

        if (!response.ok) {
          fallbackDiscovery('shopify-discovery-http');
          return;
        }

        const payload = await response.json();
        const suggestions = Array.isArray(payload?.suggestions) ? payload.suggestions : [];
        const best = suggestions
          .filter((item) => item?.parts)
          .sort((a, b) => Number(b.confidence || 0) - Number(a.confidence || 0))[0];

        const detectedCountry = upperCountry(
          payload?.detected_values?.country?.handle ||
            payload?.detected_values?.country?.iso_code ||
            best?.parts?.country?.handle ||
            best?.parts?.country?.iso_code ||
            ''
        );

        if (!applyGeoPolicy(detectedCountry, 'shopify-discovery')) {
          fallbackDiscovery('shopify-discovery-empty');
        }
      } catch (error) {
        fallbackDiscovery(error?.name === 'AbortError' ? 'shopify-discovery-timeout' : 'shopify-discovery-error');
      } finally {
        window.clearTimeout(timeout);
      }
    };

    if (previewRequested) {
      const [previewLanguageRaw, previewCountryRaw] = String(previewRequested).split('-');
      const previewLanguage = baseCode(findSupportedLanguage(previewLanguageRaw)?.code || 'en');
      const previewCountry = countryMap.has(upperCountry(previewCountryRaw))
        ? upperCountry(previewCountryRaw)
        : previewLanguage === 'fr' && countryMap.has('FR')
          ? 'FR'
          : current.country;

      openRecommendation({
        country: previewCountry,
        language: previewLanguage,
        uiLanguage: previewLanguage,
        detectedCountry: previewCountry,
        reason: 'preview',
      });
      return;
    }

    if (config.designMode) {
      publishReady({ reason: 'design-mode' });
      return;
    }

    /* A deliberate selection from the permanent theme selector remains the
       strongest preference and is allowed to follow the visitor across
       locations. Recommendation choices are intentionally not stored here. */
    if (hasManualPreference && applyStoredPreference()) return;

    /* Direct /fr or /en links are deliberate and should not be interrupted.
       If Shopify itself redirected a bare-domain request to a remembered
       localized path, however, discovery still runs so a visitor who has
       returned to Germany/Austria is not trapped in an old English/French
       recommendation. */
    if (directLocalizedEntry) {
      publishReady({ reason: 'explicit-language-url' });
      return;
    }

    publishState({
      status: 'discovering',
      targetCountry: current.country,
      targetLanguage: current.language,
      uiLanguage: current.language,
      reason: 'shopify-discovery',
    });

    querySuggestions();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
